import { motion } from 'framer-motion';
import { initialGoDown, initialGoUp, transition, whileInView } from '../../../helpers/animations';
import { useTranslation } from 'react-i18next';
import { getJourneyCardIndexes, useHomeTranslationsQuery } from '../../../services/HomePageServices';
import locationLogo from '../../../assets/images/icon/icon-location.svg';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import { Container } from 'react-bootstrap';
import { sanitizeCmsHtml } from '../../../helpers/sanitizeCmsHtml';

type TimelineState = 'completed' | 'active' | 'upcoming';

type TimelineCardContent = {
  img: string;
  title: string;
  text: string;
  time_range: string;
  venue: string;
  state: TimelineState;
  lineColor: string;
  dotColor: string;
  accentColor: string;
};

const TIMELINE_ITEMS_COUNT = 4;

const timelineToneColors = {
  green: {
    lineColor: '#367878',
    dotColor: '#006564',
    accentColor: '#006564'
  },
  grey: {
    lineColor: '#BCBEC0',
    dotColor: '#979797',
    accentColor: '#979797'
  }
};

function JourneyTimeline() {
  const { t } = useTranslation('journey');
  const homeQuery = useHomeTranslationsQuery();
  const timelineCardIndexes = homeQuery.data ? getJourneyCardIndexes(homeQuery.data) : undefined;
  const isLoading = homeQuery.isLoading;
  const isError = homeQuery.isError;

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getOptionalTranslation = (key: string, fallback = '') => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const getOptionalTranslationFromKeys = (keys: string[], fallback = '') => {
    for (const key of keys) {
      const value = t(key);
      if (value !== key && value.trim() !== '') {
        return value;
      }
    }

    return fallback;
  };

  const timelineIndexes = timelineCardIndexes ?? Array.from({ length: TIMELINE_ITEMS_COUNT }, (_, index) => index);
  const timelineCountRaw = getOptionalTranslation('timeline_count', '').trim();
  const timelineCountFromCms = Number(timelineCountRaw);
  const timelineCount = timelineCountRaw !== '' && Number.isFinite(timelineCountFromCms)
    ? Math.min(timelineIndexes.length, Math.max(0, timelineCountFromCms))
    : timelineIndexes.length;

  const timelineCards: TimelineCardContent[] = timelineIndexes.map((index) => {
    const timelineTone = index < timelineCount ? 'grey' : 'green';
    const toneColors = timelineToneColors[timelineTone];

    return {
      img: getOptionalTranslationFromKeys([
        `timeline_card_image_link_${index}`,
        `timeline_card_icon_link_${index}`
      ]),
      title: getOptionalTranslationFromKeys([
        `timeline_card_title_${index}`,
        `timeline_card_name_${index}`
      ]),
      text: getOptionalTranslationFromKeys([
        `timeline_card_description_${index}`,
        `timeline_card_text_${index}`,
        `timeline_card_content_${index}`
      ]),
      time_range: getOptionalTranslationFromKeys([
        `timeline_card_time_range_${index}`,
        `timeline_card_date_${index}`
      ]),
      venue: getOptionalTranslationFromKeys([
        `timeline_card_venue_${index}`,
        `timeline_card_location_${index}`
      ]),
      state: timelineTone === 'green' ? 'completed' : 'upcoming',
      lineColor: toneColors.lineColor,
      dotColor: toneColors.dotColor,
      accentColor: toneColors.accentColor
    };
  });

  if (isLoading || isError) {
    return null;
  }

  const journeyHeader = (
    <div className="journey__col">
      <motion.div
        initial={initialGoDown}
        whileInView={whileInView}
        viewport={{
          once: true,
          amount: 0.2
        }}
        transition={transition}
        className="journey__heading"
      >
        {t('timeline_section_name')}
      </motion.div>
      <motion.div
        initial={initialGoDown}
        whileInView={whileInView}
        viewport={{
          once: true,
          amount: 0.2
        }}
        transition={transition}
        className="journey__title"
      >
        {t('timeline_title')}
      </motion.div>
    </div>
  );

  return (
    <section id="journey" className="journey-timeline">
      <Container className='header-container'>
        {journeyHeader}
      </Container>
      <Container className="content-container">
        {isMobile
          ? <JourneyTimelineMobile cards={timelineCards} />
          : <JourneyTimelineDesktop cards={timelineCards} />}
      </Container>
    </section>
  );
}

function JourneyCard({ card, index }: { card: TimelineCardContent; index: number }) {
  const customStyle = {
    '--timeline-line-color': card.lineColor,
    '--timeline-dot-color': card.dotColor,
    '--timeline-accent-color': card.accentColor
  } as CSSProperties;

  return (
    <motion.div
      initial={initialGoUp}
      whileInView={whileInView}
      viewport={{
        once: true,
        amount: 0.2
      }}
      transition={transition}
      className="journey-timeline__desktop-item"
      style={customStyle}
    >
      <div className='journey-timeline__desktop-icon-wrap'>
        {card.img && <img src={card.img} alt={card.title} className='journey-timeline__icon' loading="lazy" decoding="async" />}
      </div>
      <div className='journey-timeline__desktop-date'>{card.time_range}</div>
      <div className={`journey-timeline__desktop-track${index === TIMELINE_ITEMS_COUNT - 1 ? ' journey-timeline__desktop-track--last' : ''}`}>
        <span className='journey-timeline__desktop-dot' />
      </div>
      <div className='journey-timeline__desktop-title'>{card.title}</div>
      <div className='journey-timeline__desktop-description'>
        <div className="journey-timeline__card-textbox__text">{parse(sanitizeCmsHtml(card.text))}</div>
      </div>
      <div className='journey-timeline__desktop-venue'>
        <img src={locationLogo} alt='location logo' loading="lazy" decoding="async" />
        <div className="journey-timeline__card-textbox__detail">{card.venue}</div>
      </div>
    </motion.div>
  );
}

function JourneyTimelineDesktop({ cards }: { cards: TimelineCardContent[] }) {
  const desktopRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const desktopElement = desktopRef.current;

    if (!desktopElement) {
      return;
    }

    const applyAlignedHeights = () => {
      const setSharedHeight = (selector: string, cssVariableName: string, fallback: number) => {
        const elements = Array.from(desktopElement.querySelectorAll<HTMLElement>(selector));
        const maxHeight = Math.max(fallback, ...elements.map((element) => element.offsetHeight));
        desktopElement.style.setProperty(cssVariableName, `${maxHeight}px`);
      };

      setSharedHeight('.journey-timeline__desktop-date', '--journey-date-height', 48);
      setSharedHeight('.journey-timeline__desktop-title', '--journey-title-height', 41);
      setSharedHeight('.journey-timeline__desktop-description', '--journey-description-height', 92);
      setSharedHeight('.journey-timeline__desktop-venue', '--journey-venue-height', 24);
    };

    window.requestAnimationFrame(() => {
      applyAlignedHeights();
      window.setTimeout(applyAlignedHeights, 0);
    });
  }, [cards]);

  return (
    <div className="journey-timeline__desktop" role="list" ref={desktopRef}>
      {cards.map((card, index) => (
        <JourneyCard
          key={`${card.title}-${index}`}
          card={card}
          index={index}
        />
      ))}
    </div>
  );
}

function JourneyTimelineMobile({ cards }: { cards: TimelineCardContent[] }) {
  return (
    <div className="journey-timeline__mobile" role="list">
      {cards.map((card, index) => {
        const customStyle = {
          '--timeline-line-color': card.lineColor,
          '--timeline-dot-color': card.dotColor,
          '--timeline-accent-color': card.accentColor
        } as CSSProperties;

        return (
          <motion.div
            key={`${card.title}-${index}`}
            initial={initialGoUp}
            whileInView={whileInView}
            viewport={{
              once: true,
              amount: 0.2
            }}
            transition={transition}
            className="journey-timeline__mobile-item"
            style={customStyle}
          >
            <div className={`journey-timeline__mobile-track${index === 0 ? ' journey-timeline__mobile-track--first' : ''}${index === cards.length - 1 ? ' journey-timeline__mobile-track--last' : ''}`}>
              <span className='journey-timeline__mobile-dot' />
            </div>
            <div className='journey-timeline__mobile-content'>
              <div className='journey-timeline__mobile-header'>
                {card.img && <img src={card.img} alt={card.title} className='journey-timeline__icon' loading="lazy" decoding="async" />}
                <div>
                  <div className='journey-timeline__mobile-title'>{card.title}</div>
                  <div className='journey-timeline__mobile-date'>{card.time_range}</div>
                </div>
              </div>
              <div className='journey-timeline__mobile-description'>
                <div className="journey-timeline__card-textbox__text">{parse(sanitizeCmsHtml(card.text))}</div>
              </div>
              <div className='journey-timeline__mobile-venue'>
                <img src={locationLogo} alt='location logo' loading="lazy" decoding="async" />
                <div className="journey-timeline__card-textbox__detail">{card.venue}</div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default JourneyTimeline;
